import { useCallback, useState } from 'react';
import type { LoaderFunctionArgs } from 'react-router';
import { redirect, useLoaderData } from 'react-router';
import { postAccountIntegrateExecuteWithToken } from '../../api/apiClient';
import { auth0Client } from '../../utils/auth/auth0Client';
import { Unauthorized } from '../../components/Error/Unauthorized';
import { IntegrationComplete } from '../../features/integration/components/IntegrationComplete';
import { IntegrationExecuteConsent } from '../../features/integration/components/IntegrationExecuteConsent';
import { IntegrationGuarantorEmailConflict } from '../../features/integration/components/IntegrationGuarantorEmailConflict';
import { isHighschoolEmail } from '../../features/highschool';

export async function integrationVerifyLoader({
  request,
}: LoaderFunctionArgs): Promise<{ status: number; token?: string }> {
  const url = new URL(request.url);

  // silent に認証を行いキャッシュではなく現在の user を取得する
  await auth0Client.checkSession();
  const user = await auth0Client.getUser();

  if (!user) {
    // セッションの無いブラウザでメールリンクを開いた場合は、ログイン完了後に token 付きの本画面へ戻して統合を再開する
    await auth0Client.loginWithRedirect({
      appState: { returnTo: `${url.pathname}${url.search}` },
      authorizationParams: {
        redirect_uri: `${window.location.origin}/home`,
      },
    });
    // loginWithRedirect が成功すれば画面を離れるため到達しない
    // component が render されないため、意味文脈として throw redirect をする
    throw redirect('/login');
  }
  if (!isHighschoolEmail(user.email)) {
    throw redirect('/unauthorized');
  }

  try {
    // 統合の実行はユーザーの同意後に行うため、loader では token の受け渡しのみを行う
    const token = url.searchParams.get('token');
    if (!token) {
      return { status: 401 };
    }

    return { status: 200, token };
  } finally {
    // search param を削除
    history.replaceState(null, '', location.pathname);
  }
}

export function IntegrationVerifyPage() {
  const { status, token } = useLoaderData<typeof integrationVerifyLoader>();
  const [executeStatus, setExecuteStatus] = useState<number | undefined>(undefined);
  const [returnUrl, setReturnUrl] = useState<string | undefined>(undefined);

  const execute = useCallback(async () => {
    if (!token) {
      return;
    }
    try {
      const { data, response } = await postAccountIntegrateExecuteWithToken({ token });
      // 完了画面を必ず挟み、ログイン元アプリへ戻るかどうかはユーザーの操作に委ねる。
      // returnUrl はログイン元アプリを特定できない場合 (best-effort) に省略され、その場合は戻り先をマイページ内とする
      setReturnUrl(data?.returnUrl);
      setExecuteStatus(response.status);
    } catch {
      // 統合実行中のエラーは認証エラーとして処理する
      setExecuteStatus(401);
    }
  }, [token]);

  if (status !== 200 || !token) {
    return <Unauthorized />;
  }

  switch (executeStatus) {
    // 未実行の場合は同意画面を表示し、同意を得てから統合を実行する
    case undefined: {
      return <IntegrationExecuteConsent execute={execute} />;
    }
    case 201: {
      return <IntegrationComplete returnUrl={returnUrl} />;
    }
    // 保護者メール衝突の場合のみ明示的にユーザに知らせる
    case 409: {
      return <IntegrationGuarantorEmailConflict />;
    }
    default: {
      return <Unauthorized />;
    }
  }
}
